import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Popover } from 'antd-v5';
import { RootState } from 'app/AppStore';
import { FileAltIcon } from 'assets/icons';
import { AddToEstimateButton } from 'components/AddToEstimate';
import { useDisclosure } from 'components/Dialog';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { getCurrentEstimateId, getEstimateVehicleKey } from 'domains/estimate/Estimate.store';
import { POPOVER_ZINDEX } from 'styles';
import { BlackButton, Flex, Icon } from 'UI';
import AddToEstimatePopover from './Popover/AddToEstimatePopover';

interface AddToEstimateButtonAndDialogProps {
  handleAddToEstimateClick: (quantity?: number) => void;
  isVehicleCatalog?: boolean;
  size?: 'sm' | 'lg';
  disabled?: boolean;
  displayed?: boolean;
  buttonComponent?: 'icon' | 'button' | ReactNode;
  initCount?: number;
  sellerNumberHandler?: string;
  sellerNameHandler?: string;
}

export const AddToEstimateButtonAndDialog = ({
  handleAddToEstimateClick,
  isVehicleCatalog = false,
  size = 'sm',
  disabled = false,
  displayed = true,
  buttonComponent = 'icon',
  initCount = 1,
  sellerNumberHandler,
  sellerNameHandler,
}: Readonly<AddToEstimateButtonAndDialogProps>) => {
  const disclosure = useDisclosure();
  const { t } = useTranslation();
  const { onOpen, onClose, isOpen } = disclosure;
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const estimateId = useSelector(getCurrentEstimateId);
  const estimateVehicleKey = useSelector((state: RootState) => getEstimateVehicleKey(state, estimateId));

  if (!displayed) {
    return <></>;
  }

  const openInCatalog = isVehicleCatalog && estimateVehicleKey !== undefined;

  const renderButton = () => {
    if (buttonComponent === 'icon') {
      return <AddToEstimateButton handleAddToEstimateClick={onOpen} size={size} disabled={disabled} />;
    } else if (buttonComponent === 'button') {
      return (
        <BlackButton onClick={onOpen} disabled={disabled}>
          <Flex gap={8} align={'center'}>
            <Icon IconComponent={FileAltIcon} size={20} />
            {t('catalog.action.add_to_estimate', 'Add to estimate')}
          </Flex>
        </BlackButton>
      );
    } else {
      return (
        <Flex onClick={onOpen} data-cy={'add-to-estimate-button'}>
          {buttonComponent}
        </Flex>
      );
    }
  };

  return (
    <Popover
      content={
        <AddToEstimatePopover
          handleClose={onClose}
          handleAddToEstimateClick={handleAddToEstimateClick}
          vehicleDetail={vehicleDetail}
          openInCatalog={openInCatalog}
          initCount={initCount}
          sellerNumberHandler={sellerNumberHandler}
          sellerNameHandler={sellerNameHandler}
        />
      }
      zIndex={POPOVER_ZINDEX}
      arrow={false}
      open={isOpen}
      defaultOpen={false}
      placement={'left'}
      trigger="click"
      onOpenChange={onClose}
    >
      {renderButton()}
    </Popover>
  );
};
